import { PageHero, SHELL, Reveal, Mono, StatusBadge, Btn } from "@/components/bits";
import { ArrowRight } from "lucide-react";

const FIELDS = [
    ["VERA ASSET ID", "VRA-0000-EXAMPLE"],
    ["ASSET TYPE", "REAL ESTATE"],
    ["VERIFICATION", "VERIFIED"],
    ["ISSUER", "EXAMPLE"],
    ["VALUATION", "DATA DEPENDENT"],
    ["CASH FLOW", "AVAILABLE"],
    ["JURISDICTION", "EXAMPLE"],
    ["STATUS", "ACTIVE"],
];

const LIFECYCLE = [
    ["ORIGINATE", "An asset with real economic activity is identified."],
    ["VERIFY", "Existence, ownership, economics and documentation are checked."],
    ["REGISTER", "The asset receives a stable VERA Asset ID."],
    ["REPRESENT", "The verified asset is represented onchain."],
];

export default function Assets() {
    return (
        <div data-testid="assets-page">
            <PageHero
                label="ASSET LAYER"
                title={"THE ASSET LAYER"}
                sub="Every asset begins with an identity. The VERA Asset Registry is where real-world assets become addressable, verifiable objects — the foundation of programmable capital."
            />
            <section className="py-20 md:py-28">
                <SHELL>
                    <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
                        <div>
                            <Reveal>
                                <p className="text-vera-mut text-base leading-relaxed mb-8">
                                    Tokenization without verification is just a wrapper around uncertainty. VERA reverses the order: an asset enters the
                                    layer only after its identity, economics and documentation are checked — then it is registered, represented and
                                    structured.
                                </p>
                            </Reveal>
                            <div className="space-y-0">
                                {LIFECYCLE.map(([t, d], i) => (
                                    <Reveal key={t} delay={i * 0.05}>
                                        <div className="flex items-start gap-6 py-4 border-b border-white/10">
                                            <span className="font-mono text-[10px] text-vera-gold mt-1">{String(i + 1).padStart(2, "0")}</span>
                                            <div>
                                                <p className="font-mono text-[11px] tracking-[0.25em] text-vera-white mb-1.5">{t}</p>
                                                <p className="text-sm text-vera-mut">{d}</p>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </div>

                        <Reveal delay={0.1}>
                            <div className="relative glass-card overflow-hidden" data-testid="demo-asset-card">
                                <span className="absolute top-6 right-[-38px] rotate-45 font-mono text-[9px] tracking-[0.3em] bg-vera-gold/20 text-vera-gold2 border border-vera-gold/40 px-10 py-1">
                                    EXAMPLE
                                </span>
                                <div className="p-7 md:p-9">
                                    <div className="flex items-center justify-between mb-8">
                                        <Mono className="text-vera-gold2">VERA ASSET ID</Mono>
                                        <StatusBadge status="DEMO" />
                                    </div>
                                    <dl>
                                        {FIELDS.map(([k, v]) => (
                                            <div key={k} className="flex items-center justify-between gap-4 py-3 border-b border-white/5">
                                                <dt className="font-mono text-[10px] tracking-[0.2em] text-vera-mut">{k}</dt>
                                                <dd className={`font-mono text-[10px] tracking-[0.15em] text-right ${k === "STATUS" ? "text-[#10B981]" : "text-vera-white"}`}>{v}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                    <p className="text-[11px] text-vera-mut/70 leading-relaxed mt-6">
                                        This record is an EXAMPLE, shown to illustrate the registry concept. VERA does not publish live asset records, and
                                        valuation is data-dependent — never invented.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal>
                        <div className="flex flex-wrap items-center justify-between gap-6 border border-vera-blue/30 bg-vera-blue/5 px-8 py-7">
                            <p className="text-vera-white/90 text-sm sm:text-base font-medium">Read the full Asset Registry documentation.</p>
                            <Btn variant="ghost" to="/docs" testid="assets-docs-link" className="!px-5 !py-3">
                                OPEN DOCS <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </Btn>
                        </div>
                    </Reveal>
                </SHELL>
            </section>
        </div>
    );
}
